import { ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TableBody,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddOutlined, AutorenewOutlined, CloseOutlined, ContentCopyOutlined, InfoOutlined, SwapHorizOutlined } from "@mui/icons-material";
import BotonVolver from "../../components/kit/BotonVolver";
import EstadoChip from "../../components/kit/EstadoChip";
import EnlaceTema from "../../components/tema/EnlaceTema";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import { Tarjeta, TituloTarjeta } from "../../components/kit/Tarjeta";
import { tokens } from "../../theme";
import {
  useAplicarComisiones,
  useCargaComisiones,
  useCohortesComision,
  useCopiarComisiones,
  useDistribucionActual,
  useDistribuirComisiones,
  useGuardarOptimizador,
  useOptimizador,
} from "../../queries/useComisiones";
import { usePasos } from "../../queries/usePasos";
import { CohorteComision, EvaluadorAsignado, FilaPropuesta, OptimizadorConfig, ResultadoDistribucion } from "../../types/comision";
import { mensajeDeError } from "../../utils/errores";
import { estiloDeInstancia } from "../../utils/estados";

/** La carga de una persona: lo que arrastra fuera de la cohorte más lo que lleva en ella. */
interface CargaPersona {
  id: number;
  nombre: string;
  puede: boolean;
  otras: number;
  cohorte: number; // en la propuesta que está en pantalla, con los cambios sin guardar
  guardada: number; // en esta cohorte, como está guardada
}

/** La comisión de un estudiante partida en columnas: guía, coguía y los integrantes asignados. */
function partir(f: FilaPropuesta) {
  const guia = f.evaluadores.find((e) => e.forzado && e.rol === "guia");
  const coguia = f.evaluadores.find((e) => e.forzado && e.rol === "coguia");
  const integrantes = f.evaluadores.filter((e) => e !== guia && e !== coguia);
  return { guia, coguia, integrantes };
}

/** En cuántas comisiones de la cohorte está cada persona. Sin `pendientes`, solo lo guardado. */
function contar(r: ResultadoDistribucion | undefined | null, pendientes: boolean) {
  const n = new Map<number, number>();
  for (const f of r?.propuesta ?? []) {
    for (const e of f.evaluadores) {
      if (!pendientes && e.pendiente) continue;
      n.set(e.id, (n.get(e.id) ?? 0) + 1);
    }
  }
  return n;
}

/** Quiénes integran cada comisión, para saber si lo que hay en pantalla difiere de lo guardado. */
function firmas(r: ResultadoDistribucion | undefined | null, pendientes: boolean) {
  return new Map(
    (r?.propuesta ?? []).map((f) => [
      f.instancia_id,
      f.evaluadores
        .filter((e) => pendientes || !e.pendiente)
        .map((e) => e.id)
        .sort((a, b) => a - b)
        .join(","),
    ]),
  );
}

/**
 * Administrar la comisión de una cohorte (etapa + período), en pantalla propia.
 *
 * Una fila por estudiante y una columna por integrante: el guía y el coguía, cuando el
 * paso los incluye, van fijos; los integrantes asignados se cambian. A la derecha, la
 * carga de cada docente —lo que ya arrastra en otras cohortes más lo de esta— se
 * recalcula con cada cambio, antes de guardar.
 *
 * Nada se escribe hasta «Guardar cambios»: el optimizador y «Traer de otro paso» dejan
 * una propuesta para revisar, igual que los ajustes a mano.
 */
export default function ComisionCohorte() {
  const params = useParams();
  const clave = { paso: Number(params.paso), ano: Number(params.ano), semestre: Number(params.semestre) };
  const { data: cohortes, isLoading } = useCohortesComision();
  const cohorte = cohortes?.find((c) => c.paso === clave.paso && c.ano === clave.ano && c.semestre === clave.semestre);

  return (
    <Box>
      <BotonVolver fallback="/app/comisiones" etiquetaFallback="Comisiones" />
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !cohorte ? (
        <Alert severity="info">Esta cohorte no tiene procesos abiertos a la espera de comisión.</Alert>
      ) : (
        <Administrar cohorte={cohorte} />
      )}
    </Box>
  );
}

function Administrar({ cohorte }: { cohorte: CohorteComision }) {
  const clave = { paso: cohorte.paso, ano: cohorte.ano, semestre: cohorte.semestre };
  const distribuir = useDistribuirComisiones();
  const copiar = useCopiarComisiones();
  const aplicar = useAplicarComisiones();
  const { data: cargaFuera = [] } = useCargaComisiones(clave);
  const tieneDistribucion = cohorte.con_comision > 0;
  // La propuesta se siembra una sola vez, así que tiene que ser con lo que hay hoy en
  // el servidor y no con lo que quedó en caché de la última vez que se abrió: eso es
  // lo de antes de guardar, y la pantalla volvía a mostrar la comisión vieja.
  const { data: actual, isFetchedAfterMount, refetch } = useDistribucionActual(clave, true);
  const cargandoActual = !isFetchedAfterMount;
  const [propuesta, setPropuesta] = useState<ResultadoDistribucion | null>(null);
  const [guardado, setGuardado] = useState(false);
  const [mostrarParams, setMostrarParams] = useState(false);
  const [mostrarTraer, setMostrarTraer] = useState(false);
  // Reasignación manual en curso: instancia + evaluador que se reemplaza (-1 = asignar nuevo).
  const [mover, setMover] = useState<{ instanciaId: number; desde: number } | null>(null);
  // La persona sobre la que está el puntero, en la tabla o en la carga: se marca en las dos.
  const [resaltado, setResaltado] = useState<number | null>(null);

  useEffect(() => {
    if (actual && isFetchedAfterMount && !propuesta) setPropuesta(actual);
  }, [actual, isFetchedAfterMount, propuesta]);

  const nuevaPropuesta = (data: ResultadoDistribucion) => {
    setPropuesta(data);
    setMover(null);
    setGuardado(false);
  };

  // Genera (o re-genera) con el optimizador. Si ya había distribución, re-distribuye todo.
  const generar = () =>
    distribuir.mutate({ ...clave, sobrescribir: tieneDistribucion, commit: false }, { onSuccess: nuevaPropuesta });

  // Trae la comisión de otro paso, calzando por estudiante. Igual que el optimizador:
  // deja una propuesta en pantalla y no escribe nada hasta "Guardar cambios".
  const traer = (origen: number) =>
    copiar.mutate(
      { ...clave, origen },
      {
        onSuccess: (data) => {
          nuevaPropuesta(data);
          setMostrarTraer(false);
        },
      },
    );

  // Reemplaza (o agrega, si `desde` no está) el evaluador de una instancia en la
  // propuesta local. Por id: el RUT no identifica a un coguía externo, que no tiene.
  const reasignar = (instanciaId: number, desde: number, to: CargaPersona) => {
    setGuardado(false);
    setPropuesta((prev) => {
      if (!prev) return prev;
      const nuevo: EvaluadorAsignado = { id: to.id, rut: null, nombre: to.nombre, carga: 0 };
      return {
        ...prev,
        propuesta: prev.propuesta.map((f) => {
          if (f.instancia_id !== instanciaId) return f;
          const existe = f.evaluadores.some((e) => e.id === desde);
          return {
            ...f,
            evaluadores: existe ? f.evaluadores.map((e) => (e.id === desde ? nuevo : e)) : [...f.evaluadores, nuevo],
          };
        }),
      };
    });
  };

  // Saca a un integrante de una comisión en la propuesta local; el cupo queda para asignar.
  const quitar = (instanciaId: number, id: number) => {
    setGuardado(false);
    setPropuesta((prev) =>
      prev && {
        ...prev,
        propuesta: prev.propuesta.map((f) =>
          f.instancia_id === instanciaId ? { ...f, evaluadores: f.evaluadores.filter((e) => e.id !== id) } : f,
        ),
      },
    );
  };

  // Publica exactamente lo que está en la propuesta (incluidos los ajustes manuales).
  // La pantalla se queda: vuelve a leer lo guardado, así los pendientes dejan de serlo.
  const publicar = () => {
    if (!propuesta) return;
    const asignaciones = propuesta.propuesta.map((f) => ({ instancia_id: f.instancia_id, ids: f.evaluadores.map((e) => e.id) }));
    aplicar.mutate(asignaciones, {
      onSuccess: () =>
        refetch().then((r) => {
          if (r.data) setPropuesta(r.data);
          setGuardado(true);
        }),
    });
  };

  const sucio = useMemo(() => {
    if (!propuesta || !actual) return false;
    const guardadas = firmas(actual, false);
    return [...firmas(propuesta, true)].some(([id, f]) => guardadas.get(id) !== f);
  }, [propuesta, actual]);

  // Cerrar la pestaña o recargar con cambios sin guardar los perdería sin decir nada.
  useEffect(() => {
    if (!sucio) return;
    const avisar = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [sucio]);

  const cargas = useMemo(() => {
    const enPropuesta = contar(propuesta, true);
    const enGuardada = contar(actual, false);
    const personas = new Map<number, CargaPersona>();
    const fila = (id: number, nombre: string): CargaPersona => ({
      id,
      nombre,
      puede: false,
      otras: 0,
      cohorte: enPropuesta.get(id) ?? 0,
      guardada: enGuardada.get(id) ?? 0,
    });
    for (const c of cargaFuera) personas.set(c.id, { ...fila(c.id, c.nombre), puede: c.puede_comision, otras: c.otras });
    // Quien está en la cohorte sin venir en la carga (un guía que no evalúa en otras).
    for (const r of [propuesta, actual]) {
      for (const f of r?.propuesta ?? []) {
        for (const e of f.evaluadores) if (!personas.has(e.id)) personas.set(e.id, fila(e.id, e.nombre));
      }
    }
    return personas;
  }, [cargaFuera, propuesta, actual]);

  const filaMover = mover ? propuesta?.propuesta.find((f) => f.instancia_id === mover.instanciaId) : null;
  const candidatos = useMemo(() => {
    if (!filaMover) return [];
    const bloqueados = new Set([...filaMover.excluidos, ...filaMover.evaluadores.map((e) => e.id)]);
    return [...cargas.values()]
      .filter((c) => c.puede && !bloqueados.has(c.id))
      .sort((a, b) => a.otras + a.cohorte - (b.otras + b.cohorte) || a.nombre.localeCompare(b.nombre, "es"));
  }, [filaMover, cargas]);

  const ocupado = distribuir.isPending || aplicar.isPending || copiar.isPending;
  const sinProcesos = cohorte.total + cohorte.sin_entregar === 0;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { md: "flex-end" } }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {cohorte.paso_nombre}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cohorte.ano} · {cohorte.semestre_display} · {cohorte.total + cohorte.sin_entregar} estudiantes (
            {cohorte.sin_entregar} sin entregar) · {cohorte.tamano_comision} integrantes por comisión
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
          {sucio && !ocupado && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Cambios sin guardar
            </Typography>
          )}
          {/* Traer de otro paso es la alternativa al optimizador: sirve igual sobre una
              cohorte recién poblada que sobre una propuesta ya armada. */}
          <Button variant="outlined" startIcon={<ContentCopyOutlined />} onClick={() => setMostrarTraer(true)} disabled={ocupado || cargandoActual}>
            {copiar.isPending ? "Trayendo…" : "Traer de otro paso"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AutorenewOutlined />}
            onClick={() => setMostrarParams(true)}
            disabled={ocupado || cargandoActual || sinProcesos}
          >
            {distribuir.isPending ? "Distribuyendo…" : tieneDistribucion ? "Re-distribuir todo" : "Distribuir"}
          </Button>
          <Button variant="contained" onClick={publicar} disabled={ocupado || cargandoActual || !sucio}>
            {aplicar.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </Stack>
      </Stack>

      {distribuir.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mensajeDeError(distribuir.error, "No se pudo distribuir.")}
        </Alert>
      )}
      {aplicar.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mensajeDeError(aplicar.error, "No se pudieron guardar los cambios.")}
        </Alert>
      )}
      {copiar.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mensajeDeError(copiar.error, "No se pudieron traer las comisiones del otro paso.")}
        </Alert>
      )}
      {guardado && !sucio && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setGuardado(false)}>
          Cambios guardados.
        </Alert>
      )}

      {!propuesta ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 290px" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {propuesta.avisos.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Stack spacing={0.25}>
                  {propuesta.avisos.map((a, i) => (
                    <span key={i}>{a}</span>
                  ))}
                </Stack>
              </Alert>
            )}
            {propuesta.propuesta.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay procesos abiertos en esta cohorte.
              </Typography>
            ) : (
              <TablaComisiones
                filas={propuesta.propuesta}
                tamano={propuesta.tamano}
                resaltado={resaltado}
                onResaltar={setResaltado}
                onMover={setMover}
                onQuitar={quitar}
              />
            )}
          </Box>
          <PanelCarga cargas={cargas} resaltado={resaltado} onResaltar={setResaltado} />
        </Box>
      )}

      {mostrarTraer && (
        <TraerDeOtroPasoDialog
          cohorte={cohorte}
          hayPropuesta={!!propuesta}
          cargando={copiar.isPending}
          onCancel={() => setMostrarTraer(false)}
          onTraer={traer}
        />
      )}

      {mostrarParams && (
        <ParamsOptimizadorDialog
          onCancel={() => setMostrarParams(false)}
          onConfirm={() => {
            setMostrarParams(false);
            generar();
          }}
          textoConfirmar={tieneDistribucion ? "Re-distribuir" : "Distribuir"}
        />
      )}

      {mover && (
        <Dialog open onClose={() => setMover(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{mover.desde === -1 ? "Asignar evaluador" : "Mover a otro evaluador"}</DialogTitle>
          <DialogContent>
            <Autocomplete
              sx={{ mt: 1 }}
              options={candidatos}
              getOptionLabel={(c) => c.nombre}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={({ key, ...props }, c) => (
                <Box component="li" key={key} {...props} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <span>{c.nombre}</span>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {c.otras + c.cohorte} en total · {c.cohorte} acá
                  </Typography>
                </Box>
              )}
              onChange={(_, value) => {
                if (value) {
                  reasignar(mover.instanciaId, mover.desde, value);
                  setMover(null);
                }
              }}
              noOptionsText="Sin evaluadores disponibles"
              renderInput={(params) => <TextField {...params} autoFocus label="Nuevo evaluador" placeholder="Buscar…" />}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              Primero los menos cargados. «Acá» es esta cohorte, con los cambios sin guardar.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMover(null)}>Cancelar</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

/** Ícono ⓘ con las categorías (áreas) del tema, pegado al final del texto. */
function IconoCategorias({ categorias }: { categorias: string[] }) {
  if (categorias.length === 0) return null;
  return (
    <Tooltip title={<Stack spacing={0.25}>{categorias.map((c) => <span key={c}>{c}</span>)}</Stack>}>
      <InfoOutlined sx={{ fontSize: 15, ml: 0.3, verticalAlign: "text-bottom", color: "text.disabled", cursor: "help" }} />
    </Tooltip>
  );
}

type OnMover = (m: { instanciaId: number; desde: number }) => void;

/** Porcentaje del ancho de la tabla para la columna del estudiante y su tema. */
const ANCHO_ESTUDIANTE = 30;

/**
 * Una fila por estudiante y una columna por integrante.
 *
 * Guía y coguía tienen columna solo si alguna comisión los trae —es decir, si el paso
 * los incluye—, y van fijos. Los integrantes asignados son tantas columnas como pida el
 * paso, o más si alguna comisión vino más grande (traída de otro paso).
 *
 * No ordena por columna: el orden es el de la cohorte (por apellido del estudiante), y
 * ordenar por un integrante no dice nada que la carga de la derecha no diga mejor.
 */
function TablaComisiones({
  filas,
  tamano,
  resaltado,
  onResaltar,
  onMover,
  onQuitar,
}: {
  filas: FilaPropuesta[];
  tamano: number;
  resaltado: number | null;
  onResaltar: (id: number | null) => void;
  onMover: OnMover;
  onQuitar: (instanciaId: number, id: number) => void;
}) {
  const partidas = filas.map((f) => ({ f, ...partir(f) }));
  const conGuia = partidas.some((p) => p.guia);
  const conCoguia = partidas.some((p) => p.coguia);
  const cupos = Math.max(tamano, ...partidas.map((p) => p.integrantes.length));

  // Anchos fijos: el estudiante se lleva una parte y el resto se reparte igual entre
  // guía, coguía e integrantes. Con anchos por contenido, cada asignación movía las
  // columnas —un nombre largo ensanchaba la suya y apretaba las demás—.
  const personas = cupos + Number(conGuia) + Number(conCoguia);
  const ancho = `${(100 - ANCHO_ESTUDIANTE) / personas}%`;
  const columnas: Columna[] = [
    { label: "Estudiante y tema", width: `${ANCHO_ESTUDIANTE}%` },
    ...(conGuia ? [{ label: "Guía", width: ancho }] : []),
    ...(conCoguia ? [{ label: "Coguía", width: ancho }] : []),
    ...Array.from({ length: cupos }, (_, i) => ({ label: cupos === 1 ? "Integrante" : `Integrante ${i + 1}`, width: ancho })),
  ];
  const persona = (e: EvaluadorAsignado, accion?: ReactNode) => (
    <Persona e={e} resaltada={e.id === resaltado} onResaltar={onResaltar} accion={accion} />
  );

  return (
    // Los nombres pueden partirse en dos líneas: el ancho mínimo es para que no se
    // aprieten más que eso, no para que quepan en una.
    <TablaKit columnas={columnas} minWidth={260 + 180 * personas} anchosFijos etiqueta="Comisiones de la cohorte">
      <TableBody>
        {partidas.map(({ f, guia, coguia, integrantes }) => (
          <FilaKit key={f.instancia_id}>
            <CeldaPrincipal>
              {/* El chip no se mueve del lado del nombre: si no caben los dos, el que se
                  parte en dos líneas es el nombre. */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>{f.estudiante}</Box>
                {!f.entregado && (
                  <Box sx={{ flexShrink: 0 }}>
                    <EstadoChip label="Sin entregar" denso {...estiloDeInstancia("pendiente")} />
                  </Box>
                )}
              </Box>
              {/* El tema va debajo, en la misma celda: es de quién y sobre qué, una sola cosa. */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  fontWeight: 400,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                }}
              >
                <EnlaceTema id={f.tema_id} titulo={f.tema} vacio="Sin tema" />
                <IconoCategorias categorias={f.categorias} />
              </Typography>
            </CeldaPrincipal>
            {conGuia && <CeldaKit atenuada={!guia}>{guia ? persona(guia) : "—"}</CeldaKit>}
            {conCoguia && <CeldaKit atenuada={!coguia}>{coguia ? persona(coguia) : "—"}</CeldaKit>}
            {Array.from({ length: cupos }, (_, i) => {
              const e = integrantes[i];
              if (e) {
                return (
                  <CeldaKit key={i}>
                    {persona(
                      e,
                      <>
                        <Tooltip title="Cambiar por otro evaluador">
                          <IconButton size="small" onClick={() => onMover({ instanciaId: f.instancia_id, desde: e.id })}>
                            <SwapHorizOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Quitar de la comisión">
                          <IconButton size="small" onClick={() => onQuitar(f.instancia_id, e.id)}>
                            <CloseOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>,
                    )}
                  </CeldaKit>
                );
              }
              // Cada cupo vacío dentro de lo que pide el paso se ofrece para completar.
              return (
                <CeldaKit key={i} atenuada>
                  {i < tamano ? (
                    <Button size="small" startIcon={<AddOutlined />} onClick={() => onMover({ instanciaId: f.instancia_id, desde: -1 })}>
                      Asignar
                    </Button>
                  ) : (
                    "—"
                  )}
                </CeldaKit>
              );
            })}
          </FilaKit>
        ))}
      </TableBody>
    </TablaKit>
  );
}

/** Un integrante en su celda: nombre, si se agrega al guardar, y su acción. */
function Persona({
  e,
  resaltada,
  onResaltar,
  accion,
}: {
  e: EvaluadorAsignado;
  resaltada: boolean;
  onResaltar: (id: number | null) => void;
  accion?: ReactNode;
}) {
  // Las acciones aparecen encima del final del nombre al pasar el mouse (o al llegar
  // con el teclado), en vez de ocupar siempre su lugar: con las columnas de ancho fijo,
  // dos íconos a la vista dejaban al nombre partido a la mitad de una palabra. En
  // pantallas táctiles no hay "pasar por encima", así que ahí se ven siempre.
  return (
    <Box
      onMouseEnter={() => onResaltar(e.id)}
      onMouseLeave={() => onResaltar(null)}
      sx={{
        position: "relative",
        mx: -1,
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        bgcolor: resaltada ? tokens.marca.tenue : undefined,
        "& .acciones": { opacity: 0 },
        "&:hover .acciones, &:focus-within .acciones": { opacity: 1 },
        "@media (hover: none)": { "& .acciones": { opacity: 1, position: "static", justifyContent: "flex-end" } },
      }}
    >
      <Box sx={{ overflowWrap: "break-word" }}>
        {e.nombre}
        {e.pendiente && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            se agrega al guardar
          </Typography>
        )}
      </Box>
      {accion && (
        <Box
          className="acciones"
          sx={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            borderRadius: "4px",
            bgcolor: tokens.marca.tenue,
            transition: "opacity .15s",
          }}
        >
          {accion}
        </Box>
      )}
    </Box>
  );
}

/**
 * La carga de cada docente, al lado de la tabla.
 *
 * Es la de hoy —sus comisiones en procesos abiertos de otros pasos y períodos— más lo
 * que lleva en esta cohorte según lo que está en pantalla, así que cambia con cada
 * ajuste. Lo que difiere de lo guardado se marca, para ver qué mueve guardar.
 */
function PanelCarga({
  cargas,
  resaltado,
  onResaltar,
}: {
  cargas: Map<number, CargaPersona>;
  resaltado: number | null;
  onResaltar: (id: number | null) => void;
}) {
  const [q, setQ] = useState("");
  const lista = useMemo(() => {
    const filtro = q.trim().toLocaleLowerCase("es");
    return [...cargas.values()]
      .filter((c) => !filtro || c.nombre.toLocaleLowerCase("es").includes(filtro))
      .sort((a, b) => b.otras + b.cohorte - (a.otras + a.cohorte) || a.nombre.localeCompare(b.nombre, "es"));
  }, [cargas, q]);
  const maximo = Math.max(1, ...[...cargas.values()].map((c) => c.otras + c.cohorte));

  return (
    <Box component="aside" sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
      <Tarjeta>
        <Box>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <TituloTarjeta>Carga por docente</TituloTarjeta>
            <Tooltip
              title={
                "En cuántas comisiones de procesos abiertos está cada docente. «Esta cohorte» cuenta lo que está en " +
                "pantalla, con los cambios sin guardar; «Otras etapas» es lo que ya tiene en cualquier otra etapa o " +
                "período, y no cambia desde aquí."
              }
            >
              {/* Enfocable: quien navega con teclado también tiene que poder leerlo. */}
              <InfoOutlined tabIndex={0} sx={{ fontSize: 18, color: "text.disabled", cursor: "help" }} />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Leyenda color={tokens.naranjo[400]}>Esta cohorte</Leyenda>
            <Leyenda color={tokens.azul[300]}>Otras etapas</Leyenda>
          </Stack>
        </Box>
        <TextField size="small" placeholder="Buscar docente…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Box sx={{ maxHeight: { lg: "calc(100vh - 230px)" }, overflowY: "auto", mx: -1, px: 1 }}>
          {lista.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nadie con ese nombre.
            </Typography>
          )}
          <Stack spacing={0.5}>
            {lista.map((c) => (
              <FilaCarga key={c.id} c={c} maximo={maximo} resaltada={c.id === resaltado} onResaltar={onResaltar} />
            ))}
          </Stack>
        </Box>
      </Tarjeta>
    </Box>
  );
}

function Leyenda({ color, children }: { color: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  );
}

function FilaCarga({
  c,
  maximo,
  resaltada,
  onResaltar,
}: {
  c: CargaPersona;
  maximo: number;
  resaltada: boolean;
  onResaltar: (id: number | null) => void;
}) {
  const total = c.otras + c.cohorte;
  const delta = c.cohorte - c.guardada;
  return (
    <Box
      onMouseEnter={() => onResaltar(c.id)}
      onMouseLeave={() => onResaltar(null)}
      sx={{ px: 1, py: 0.75, mx: -1, borderRadius: "5px", bgcolor: resaltada ? tokens.marca.tenue : undefined }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
        <Typography
          variant="body2"
          sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          color={c.puede ? "text.primary" : "text.secondary"}
          title={c.puede ? c.nombre : `${c.nombre} (no está habilitado para integrar comisiones)`}
        >
          {c.nombre}
        </Typography>
        {delta !== 0 && (
          <Tooltip title={`${delta > 0 ? "+" : ""}${delta} respecto de lo guardado`}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.naranjo[600] }}>
              {delta > 0 ? `+${delta}` : `−${-delta}`}
            </Typography>
          </Tooltip>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 20, textAlign: "right" }}>
          {total}
        </Typography>
      </Stack>
      <Tooltip title={`${c.cohorte} en esta cohorte · ${c.otras} en otras etapas o períodos`} placement="left">
        <Box sx={{ display: "flex", height: 6, mt: 0.5, borderRadius: "3px", overflow: "hidden", bgcolor: tokens.tabla.filaBorde }}>
          <Box sx={{ width: `${(c.cohorte / maximo) * 100}%`, bgcolor: tokens.naranjo[400], transition: "width .2s" }} />
          <Box sx={{ width: `${(c.otras / maximo) * 100}%`, bgcolor: tokens.azul[300], transition: "width .2s" }} />
        </Box>
      </Tooltip>
    </Box>
  );
}

/** Confirmar/editar los parámetros del optimizador antes de correr el algoritmo. */
function ParamsOptimizadorDialog({ onCancel, onConfirm, textoConfirmar }: { onCancel: () => void; onConfirm: () => void; textoConfirmar: string }) {
  const { data, isLoading } = useOptimizador();
  const guardar = useGuardarOptimizador();
  const [form, setForm] = useState<OptimizadorConfig | null>(null);

  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);

  const set = (k: keyof OptimizadorConfig, v: number) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const confirmar = () => form && guardar.mutate(form, { onSuccess: onConfirm });

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Parámetros del optimizador</DialogTitle>
      <DialogContent dividers>
        {isLoading || !form ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {guardar.isError && <Alert severity="error">{mensajeDeError(guardar.error, "No se pudieron guardar los parámetros.")}</Alert>}
            <TextField
              label="Prioridad de áreas"
              type="number"
              value={form.peso_areas}
              onChange={(e) => set("peso_areas", Number(e.target.value))}
              inputProps={{ min: 0, step: 0.5 }}
              helperText="Cuánto privilegiar que el evaluador coincida en áreas con el tema."
            />
            <TextField
              label="Prioridad de equidad"
              type="number"
              value={form.peso_equidad}
              onChange={(e) => set("peso_equidad", Number(e.target.value))}
              inputProps={{ min: 0, step: 0.5 }}
              helperText="Cuánto privilegiar repartir la carga de forma pareja."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Población"
                type="number"
                value={form.poblacion}
                onChange={(e) => set("poblacion", Number(e.target.value))}
                inputProps={{ min: 4, step: 1 }}
                fullWidth
              />
              <TextField
                label="Generaciones"
                type="number"
                value={form.generaciones}
                onChange={(e) => set("generaciones", Number(e.target.value))}
                inputProps={{ min: 1, step: 10 }}
                fullWidth
                helperText="Más = mejor, más lento."
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={guardar.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={confirmar} disabled={guardar.isPending || !form}>
          {guardar.isPending ? "Guardando…" : textoConfirmar}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Elegir de qué paso traer las comisiones.
 *
 * Los prerrequisitos del paso van primero y marcados: "el paso anterior" es casi
 * siempre lo que se quiere traer, y si hay uno solo viene elegido de entrada.
 */
function TraerDeOtroPasoDialog({
  cohorte,
  hayPropuesta,
  cargando,
  onCancel,
  onTraer,
}: {
  cohorte: CohorteComision;
  hayPropuesta: boolean;
  cargando: boolean;
  onCancel: () => void;
  onTraer: (origen: number) => void;
}) {
  const { data: pasos = [] } = usePasos();
  const [elegido, setElegido] = useState<number | null>(null);

  const { opciones, previos, sugerido } = useMemo(() => {
    const actual = pasos.find((p) => p.id === cohorte.paso);
    const previos = new Set((actual?.depende_de ?? []).map((p) => p.id));
    const opciones = pasos
      .filter((p) => p.id !== cohorte.paso)
      .sort((a, b) => Number(previos.has(b.id)) - Number(previos.has(a.id)) || a.nombre.localeCompare(b.nombre));
    return { opciones, previos, sugerido: previos.size === 1 ? [...previos][0] : null };
  }, [pasos, cohorte.paso]);

  const origen = elegido ?? sugerido;

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Traer comisiones de otro paso</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          A cada estudiante de esta cohorte se le trae la comisión que tuvo en el paso que elijas. Quien no estuvo en ese
          paso queda <strong>sin comisión</strong>.
        </DialogContentText>
        <TextField
          select
          fullWidth
          size="small"
          label="Paso de origen"
          value={origen ?? ""}
          onChange={(e) => setElegido(Number(e.target.value))}
          disabled={cargando || opciones.length === 0}
        >
          {opciones.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.nombre}
              {previos.has(p.id) && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  · paso anterior
                </Typography>
              )}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          {hayPropuesta
            ? "Reemplaza lo que estás viendo. No se guarda nada hasta que aprietes “Guardar cambios”."
            : "No se guarda nada: vas a poder revisarlo y ajustarlo antes."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={cargando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => origen && onTraer(origen)} disabled={cargando || !origen}>
          {cargando ? "Trayendo…" : "Traer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
